/* Quizzz secure API client.
   Reads cookie-stored CSRF token, sends it on every state-changing request.
   All requests use credentials:'include' so the httpOnly JWT cookie is sent. */
const BACKEND = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND}/api`;

function getCookie(name) {
  const m = document.cookie.match(new RegExp("(^|; )" + name + "=([^;]+)"));
  return m ? decodeURIComponent(m[2]) : "";
}

async function request(path, { method = "GET", body, headers = {} } = {}) {
  const opts = {
    method,
    credentials: "include",
    headers: { "Content-Type": "application/json", ...headers },
  };
  if (body !== undefined) opts.body = JSON.stringify(body);
  if (method !== "GET" && method !== "HEAD") {
    const csrf = getCookie("qz_csrf");
    if (csrf) opts.headers["X-CSRF-Token"] = csrf;
  }
  const res = await fetch(`${API}${path}`, opts);
  const ct = res.headers.get("content-type") || "";
  let data = null;
  let parseFailed = false;
  try {
    if (ct.includes("application/json")) data = await res.json();
    else data = await res.text();
  } catch {
    parseFailed = true;
  }
  if (!res.ok) {
    const fallback = {
      400: "Something in your request looks off. Please review and try again.",
      401: "Invalid email or password. Please try again.",
      403: "You don't have permission to do that.",
      404: "We couldn't find what you were looking for.",
      409: "An account with that email already exists.",
      410: "That code has expired. Please request a new one.",
      413: "That file is too large. Try a smaller one.",
      415: "That file type isn't supported.",
      422: "Some of the details aren't quite right. Please review them.",
      429: "Too many tries — please wait a moment and try again.",
      500: "Something went wrong on our side. Please try again shortly.",
      502: "We couldn't send the email right now. Please try again in a moment, or check that your email address is correct.",
      503: "The service is temporarily unavailable. Please try again later.",
      504: "The request took too long. Please try again.",
    };
    let msg = (data && typeof data === "object" && (data.detail || data.message))
              || (typeof data === "string" && data)
              || fallback[res.status]
              || `Something went wrong (error ${res.status}). Please try again.`;
    const err = new Error(msg);
    err.status = res.status;
    throw err;
  }
  if (parseFailed) throw new Error("We couldn't reach the server. Please check your internet and try again.");
  return data;
}

export const api = {
  me: () => request("/auth/me"),
  signup: (name, email, password) => request("/auth/signup", { method: "POST", body: { name, email, password } }),
  requestOtp: (name, email, password) => request("/auth/request-otp", { method: "POST", body: { name, email, password } }),
  verifyOtp: (email, code) => request("/auth/verify-otp", { method: "POST", body: { email, code } }),
  forgotPassword: (email) => request("/auth/forgot-password", { method: "POST", body: { email } }),
  resetPassword: (email, code, new_password) => request("/auth/reset-password", { method: "POST", body: { email, code, new_password } }),
  login: (email, password) => request("/auth/login", { method: "POST", body: { email, password } }),
  logout: () => request("/auth/logout", { method: "POST" }),
  changePassword: (current_password, new_password) => request("/auth/change-password", { method: "POST", body: { current_password, new_password } }),
  aiQuery: (messages, system, model, extras) => request("/ai/query", { method: "POST", body: { messages, system, model, ...(extras || {}) } }),
  getQuota: () => request("/quota"),
  fetchUrl: (url) => request("/fetch-url", { method: "POST", body: { url } }),
  createAccessRequest: (name, email, note) => request("/access-requests", { method: "POST", body: { name, email, note } }),
  submitFeedback: (message, rating) => request("/feedback", { method: "POST", body: { message, rating } }),
  adminListFeedback: () => request("/admin/feedback"),
  adminDeleteFeedback: (id) => request(`/admin/feedback/${id}`, { method: "DELETE" }),
  adminListUsers: () => request("/admin/users"),
  adminDeleteUser: (id) => request(`/admin/users/${id}`, { method: "DELETE" }),
  adminListRequests: () => request("/admin/requests"),
  adminSetRequest: (id, status) => request(`/admin/requests/${id}`, { method: "PATCH", body: { status } }),
  adminListLogs: () => request("/admin/ai-logs"),
  createShare: (data) => request("/shares", { method: "POST", body: data }),
  getShare: (slug) => request(`/shares/${slug}`),
  kidsListDecks: () => request("/kids-decks"),
  adminCreateKidsDeck: (deck) => request("/admin/kids-decks", { method: "POST", body: deck }),
  adminUpdateKidsDeck: (id, deck) => request(`/admin/kids-decks/${id}`, { method: "PATCH", body: deck }),
  adminDeleteKidsDeck: (id) => request(`/admin/kids-decks/${id}`, { method: "DELETE" }),
  bankCategories: () => request("/question-bank/categories"),
  bankSample: (category, count) => request(`/question-bank?category=${encodeURIComponent(category)}&count=${count}`),
  adminBankBulkUpload: (questions) => request("/admin/question-bank/bulk", { method: "POST", body: { questions } }),
  adminBankStats: () => request("/admin/question-bank/stats"),
  adminBankDeleteCategory: (cat) => request(`/admin/question-bank/${encodeURIComponent(cat)}`, { method: "DELETE" }),
  adminSearchUsers: (q) => request(`/admin/users/search?q=${encodeURIComponent(q)}`),
  adminSetUserType: (uid, user_type, school_name) => request(`/admin/users/${uid}/type`, { method: "POST", body: { user_type, school_name } }),
  adminAdjustCredits: (uid, action, amount, note) => request(`/admin/users/${uid}/credits`, { method: "POST", body: { action, amount, note } }),
  requestOtpExt: (name, email, password, user_type, school_name, school_logo_data, school_logo_mime, interest, interest_custom) => request("/auth/request-otp", { method: "POST", body: { name, email, password, user_type, school_name, school_logo_data: school_logo_data || "", school_logo_mime: school_logo_mime || "", interest: interest || "", interest_custom: interest_custom || "" } }),
  setInterest: (interest, interest_custom) => request("/me/interest", { method: "POST", body: { interest, interest_custom: interest_custom || "" } }),
  setSchoolLogo: (school_logo_data, school_logo_mime) => request("/me/school-logo", { method: "POST", body: { school_logo_data, school_logo_mime } }),
  clearSchoolLogo: () => request("/me/school-logo", { method: "DELETE" }),
  adminSetSchoolLogo: (uid, school_logo_data, school_logo_mime) => request(`/admin/users/${uid}/school-logo`, { method: "POST", body: { school_logo_data, school_logo_mime } }),
  adminClearSchoolLogo: (uid) => request(`/admin/users/${uid}/school-logo`, { method: "DELETE" }),
  // Pricing & cost preview
  creditsQuote: (provider, model, questions, file_inventory, extracted_chars) => request("/credits/quote", { method: "POST", body: { provider, model, questions, file_inventory: file_inventory || {}, extracted_chars: Math.max(0, parseInt(extracted_chars || 0, 10) || 0) } }),
  adminGetPricing: () => request("/admin/pricing"),
  adminPutPricing: (pricing) => request("/admin/pricing", { method: "PUT", body: pricing }),
  adminCostAnalytics: (days) => request(`/admin/cost-analytics?days=${days || 30}`),
  // Promotional bonus (Feb 2026, additive — see /app/backend/promotions.py)
  promotionsActive: () => request("/promotions/active"),
  adminGetPromotion: () => request("/admin/promotions"),
  adminPutPromotion: (promo) => request("/admin/promotions", { method: "PUT", body: promo }),
  adminPromotionPreview: (promo) => request("/admin/promotions/preview", { method: "POST", body: promo }),
  // Plans & payments
  listPlans: () => request("/plans"),
  paymentsCreateOrder: (plan_id) => request("/payments/create-order", { method: "POST", body: { plan_id } }),
  paymentsVerify: (payload) => request("/payments/verify", { method: "POST", body: payload }),
  paymentsMe: () => request("/payments/me"),
  adminListPlans: () => request("/admin/plans"),
  adminCreatePlan: (plan) => request("/admin/plans", { method: "POST", body: plan }),
  adminUpdatePlan: (pid, plan) => request(`/admin/plans/${pid}`, { method: "PUT", body: plan }),
  adminDeletePlan: (pid) => request(`/admin/plans/${pid}`, { method: "DELETE" }),
  adminPayments: (days) => request(`/admin/payments?days=${days || 30}`),
  teacherListSets: () => request("/teacher/sets"),
  teacherCreateSet: (set) => request("/teacher/sets", { method: "POST", body: set }),
  teacherUpdateSet: (sid, set) => request(`/teacher/sets/${sid}`, { method: "PATCH", body: set }),
  teacherDeleteSet: (sid) => request(`/teacher/sets/${sid}`, { method: "DELETE" }),
  teacherGeneratePaper: async (formData) => {
    const csrf = getCookie("qz_csrf");
    const headers = {};
    if (csrf) headers["X-CSRF-Token"] = csrf;
    const res = await fetch(`${API}/teacher/generate-paper`, {
      method: "POST",
      credentials: "include",
      headers,
      body: formData,
    });
    let data = null;
    try { data = await res.json(); } catch {}
    if (!res.ok) {
      const msg = (data && (data.detail || data.message)) || `Generation failed (error ${res.status}).`;
      const err = new Error(msg); err.status = res.status; throw err;
    }
    return data;
  },
  teacherDownloadDocx: async (paper) => {
    const csrf = getCookie("qz_csrf");
    const headers = { "Content-Type": "application/json" };
    if (csrf) headers["X-CSRF-Token"] = csrf;
    const res = await fetch(`${API}/teacher/download-docx`, {
      method: "POST",
      credentials: "include",
      headers,
      body: JSON.stringify(paper),
    });
    if (!res.ok) {
      let msg = `Couldn't build the Word file (error ${res.status}).`;
      try { const d = await res.json(); msg = d.detail || d.message || msg; } catch {}
      const err = new Error(msg); err.status = res.status; throw err;
    }
    return await res.blob();
  },
  // Exam Pattern Library (admin)
  adminListPatterns: (exam_code = "NEET_UG", status, tag) => {
    const params = new URLSearchParams({ exam_code });
    if (status) params.set("status", status);
    if (tag) params.set("tag", tag);
    return request(`/admin/exam-patterns?${params.toString()}`);
  },
  adminUploadPattern: async ({ file, exam_code = "NEET_UG", tags = "", note = "" }) => {
    const csrf = getCookie("qz_csrf");
    const headers = {};
    if (csrf) headers["X-CSRF-Token"] = csrf;
    const fd = new FormData();
    fd.append("exam_code", exam_code);
    fd.append("tags", tags);
    fd.append("note", note);
    fd.append("file", file);
    const res = await fetch(`${API}/admin/exam-patterns/upload`, {
      method: "POST",
      credentials: "include",
      headers,
      body: fd,
    });
    let data = null; try { data = await res.json(); } catch {}
    if (!res.ok) {
      const msg = (data && (data.detail || data.message)) || `Upload failed (${res.status}).`;
      const err = new Error(msg); err.status = res.status; throw err;
    }
    return data;
  },
  adminUpdatePattern: (id, body) => request(`/admin/exam-patterns/${id}`, { method: "PATCH", body }),
  adminDeletePattern: (id) => request(`/admin/exam-patterns/${id}`, { method: "DELETE" }),
  adminAnalyzePattern: (id) => request(`/admin/exam-patterns/${id}/analyze`, { method: "POST" }),
  patternFileUrl: (id) => `${API}/admin/exam-patterns/${id}/file`,

  // ─── UPSC Intelligence System ────────────────────────────────────────────
  upscRegistry: () => request("/upsc/registry"),
  upscBankStats: () => request("/upsc/bank/stats"),
  upscAdminMetrics: () => request("/upsc/admin/metrics"),
  upscAdminListUploads: () => request("/upsc/admin/uploads"),
  upscAdminUpload: async ({ files, source_hint = "" }) => {
    const csrf = getCookie("qz_csrf");
    const headers = {};
    if (csrf) headers["X-CSRF-Token"] = csrf;
    const fd = new FormData();
    fd.append("source_hint", source_hint);
    for (const f of files) fd.append("files", f);
    const res = await fetch(`${API}/upsc/admin/upload`, {
      method: "POST",
      credentials: "include",
      headers,
      body: fd,
    });
    let data = null; try { data = await res.json(); } catch {}
    if (!res.ok) {
      const msg = (data && (data.detail || data.message)) || `Upload failed (${res.status}).`;
      const err = new Error(msg); err.status = res.status; throw err;
    }
    return data;
  },
  upscAdminProcess: (uploadId) => request(`/upsc/admin/process/${uploadId}`, { method: "POST" }),
  upscAdminProcessAll: () => request(`/upsc/admin/process-all`, { method: "POST" }),
  upscAdminDelete: (uploadId) => request(`/upsc/admin/upload/${uploadId}`, { method: "DELETE" }),
  upscAdminListPatterns: (subject) => {
    const q = subject ? `?subject=${encodeURIComponent(subject)}` : "";
    return request(`/upsc/admin/patterns${q}`);
  },
  upscAdminListBank: (subject, limit = 50) => {
    const params = new URLSearchParams({ limit: String(limit) });
    if (subject) params.set("subject", subject);
    return request(`/upsc/admin/bank?${params.toString()}`);
  },

  // ─── Traffic Analytics (admin only) ─────────────────────────────────────
  adminAnalyticsSummary: (range = "week") =>
    request(`/admin/analytics/summary?range=${encodeURIComponent(range)}`),
};
